import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";

import { roomReactionCopy, siteCopy } from "../content";
import { communityCopy } from "../content/community";
import { AppFooter } from "../components/AppFooter";
import { Dialog } from "../components/Dialog";
import { HOTSPOT_IDS, type HotspotId as SceneHotspotId } from "../config/hotspots";
import { BoardsPanel } from "../features/boards";
import { ContactForm, NewsletterForm } from "../features/contact";
import { DailyCardPanel, DailyRecipePanel } from "../features/daily-gift";
import { HearthPanel } from "../features/hearth";
import { JournalLanding } from "../features/journal/JournalLanding";
import { AboutPanel, PrivacyPanel } from "../features/legal/LegalPanels";
import { SeaCheckInPanel } from "../features/sea/SeaCheckInPanel";
import { RoomObjectList, ShackScene } from "../features/scene";
// PRANK: delete this line, <IrritantPopups /> below and src/features/prank/ to roll back.
import { IrritantPopups } from "../features/prank/IrritantPopups";
import {
  SoulCheck,
  type SoulCheckCompletion,
} from "../features/soul-check";
import { EntryGate, type EntryIntent } from "../features/threshold";
import type { CommunityView } from "../features/community";
import type {
  HotspotId,
  PanelId,
  Recommendation,
} from "./experience-state";
import styles from "./App.module.css";
import { useExperience } from "./use-experience";

/*
 * The community is the only part of the app that needs an account, and with it
 * the Supabase SDK. Loading it lazily is what keeps that weight off the
 * anonymous experience: a visitor who never walks out to the deck never
 * downloads the client and never opens a connection to it.
 */
const CommunityPanel = lazy(async () => ({
  default: (await import("../features/community")).CommunityPanel,
}));

type JournalView = "landing" | "card" | "soul-check";

/**
 * The room lives at "/", and the deck outside it has real URLs of its own —
 * the magic link in an application mail has to land somewhere, and Leonie's
 * notification links straight at one waiting application.
 */
type Route = { kind: "room" } | { kind: "community"; view: CommunityView };

function readRoute(): Route {
  if (typeof window === "undefined") return { kind: "room" };

  const path = window.location.pathname.replace(/\/$/, "");
  if (path === "/community/beheer") return { kind: "community", view: "admin" };
  if (path === "/community") return { kind: "community", view: "deck" };
  return { kind: "room" };
}

/** Which hotspot each direction points at is behaviour; the words are not. */
const recommendationHotspot: Record<Recommendation, HotspotId> = {
  special: "menu",
  "heart-to-heart": "hearth",
  "the-wave": "boards",
};

const intentSuggestion: Record<EntryIntent, HotspotId> = {
  rest: "hearth",
  direction: "journal",
  connection: "hearth",
  surprise: "menu",
};

function isHotspotPanel(panel: PanelId | null): panel is HotspotId {
  return panel !== null && HOTSPOT_IDS.includes(panel as SceneHotspotId);
}

/*
 * Stepping inside is a step forward, so it gets a history entry of its own.
 * Without one the browser's back button walks straight off the site, when
 * stepping back out of the room is what it is reached for. The URL stays "/"
 * on purpose: the whole experience lives there, and the threshold is meant to
 * be walked through rather than deep-linked past.
 */
const ROOM_HISTORY_MARKER = "soul-shack-room";

function isRoomHistoryEntry(historyState: unknown): boolean {
  return (
    typeof historyState === "object" &&
    historyState !== null &&
    (historyState as { soulShack?: unknown }).soulShack === ROOM_HISTORY_MARKER
  );
}

function directLegalPanel(): PanelId | null {
  if (typeof window === "undefined") return null;
  if (window.location.pathname.replace(/\/$/, "") === "/privacy") {
    return "privacy";
  }
  return null;
}

export function App() {
  const { state, dispatch } = useExperience();
  const [journalView, setJournalView] = useState<JournalView>("landing");
  const [soulCheck, setSoulCheck] = useState<SoulCheckCompletion | null>(null);
  const [reactionDismissed, setReactionDismissed] = useState(false);
  const [route, setRoute] = useState<Route>(readRoute);

  useEffect(() => {
    const panel = directLegalPanel();
    if (panel) dispatch({ type: "open-panel", panel });
  }, [dispatch]);

  // Kept separate from the threshold's popstate handling below: walking back
  // out of the deck is a route change, not a departure from the room.
  useEffect(() => {
    function handleRouteChange() {
      setRoute(readRoute());
    }

    window.addEventListener("popstate", handleRouteChange);
    return () => window.removeEventListener("popstate", handleRouteChange);
  }, []);

  const resetToThreshold = useCallback(() => {
    setSoulCheck(null);
    setJournalView("landing");
    setReactionDismissed(false);
    dispatch({ type: "leave" });
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [dispatch]);

  useEffect(() => {
    if (state.phase === "threshold") {
      return undefined;
    }

    function handlePopState() {
      // Anything that is no longer our room entry means the visitor stepped
      // back past the threshold, open panel and all.
      if (isRoomHistoryEntry(window.history.state)) {
        return;
      }

      resetToThreshold();
    }

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [resetToThreshold, state.phase]);

  const suggestedHotspot = useMemo<HotspotId | null>(() => {
    if (state.recommendation) {
      return recommendationHotspot[state.recommendation];
    }
    if (state.intent) return intentSuggestion[state.intent];
    return null;
  }, [state.intent, state.recommendation]);

  function openPanel(panel: PanelId) {
    dispatch({ type: "open-panel", panel });
  }

  function closePanel() {
    dispatch({ type: "close-panel" });
    setJournalView("landing");

    if (window.location.pathname.replace(/\/$/, "") === "/privacy") {
      window.history.replaceState({}, "", "/");
    }
  }

  function activateHotspot(hotspot: HotspotId) {
    if (hotspot === "journal") setJournalView("landing");
    dispatch({ type: "open-hotspot", hotspot });
  }

  function finishEntry(intent: EntryIntent | null) {
    dispatch({ type: "select-intent", intent: intent ?? undefined });
    dispatch({ type: "enter" });
    dispatch({ type: "finish-opening" });

    if (!isRoomHistoryEntry(window.history.state)) {
      window.history.pushState({ soulShack: ROOM_HISTORY_MARKER }, "");
    }
  }

  function finishSoulCheck(completion: SoulCheckCompletion) {
    setSoulCheck(completion);
    setReactionDismissed(false);
    dispatch({
      type: "complete-soul-check",
      recommendation: completion.result.direction,
    });
  }

  function openCommunity(view: CommunityView = "deck") {
    const path = view === "admin" ? "/community/beheer" : "/community";

    // Carries the room marker so the threshold's popstate handler treats a step
    // back from the deck as returning indoors, not as leaving the site.
    window.history.pushState({ soulShack: ROOM_HISTORY_MARKER }, "", path);
    setRoute({ kind: "community", view });
    window.scrollTo({ top: 0, behavior: "auto" });
  }

  function leaveCommunity() {
    window.history.pushState({ soulShack: ROOM_HISTORY_MARKER }, "", "/");
    setRoute({ kind: "room" });
  }

  function leaveShack() {
    // Leaving through the footer is the same move as the back button, so it
    // walks the room's history entry back rather than stranding it. The
    // popstate listener below then does the actual leaving.
    if (isRoomHistoryEntry(window.history.state)) {
      window.history.back();
      return;
    }

    resetToThreshold();
  }

  const activeHotspot = isHotspotPanel(state.activePanel)
    ? state.activePanel
    : suggestedHotspot;
  const reaction = state.recommendation
    ? roomReactionCopy[state.recommendation]
    : null;

  if (route.kind === "community") {
    return (
      <div className={styles.app}>
        <main id="main-content" className={styles.deck}>
          <div className={styles.deckInner}>
            <h1 className={styles.deckTitle}>
              {route.view === "admin"
                ? "Beheer van het dek"
                : communityCopy.invitation.title}
            </h1>
            <Suspense
              fallback={
                <p className={styles.deckLoading} aria-live="polite">
                  Het dek wordt geopend…
                </p>
              }
            >
              <CommunityPanel view={route.view} />
            </Suspense>
            <button
              className={styles.deckBack}
              type="button"
              onClick={leaveCommunity}
            >
              Terug naar de shack
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.app}>
      <main id="main-content" className={styles.shell}>
        {state.phase === "threshold" ? (
          <EntryGate
            initialIntent={state.intent ?? null}
            onEnter={finishEntry}
          />
        ) : (
          <>
            <ShackScene
              activeHotspotId={activeHotspot}
              discoveredHotspotIds={state.discoveredHotspots}
              onHotspotActivate={(hotspot) => activateHotspot(hotspot)}
              onSoundChange={(enabled) =>
                dispatch({ type: "set-sound", enabled })
              }
              reaction={state.recommendation ?? null}
              soundAvailable={false}
              soundEnabled={state.soundEnabled}
            />
            <IrritantPopups />
            <AppFooter
              onRoomList={() => openPanel("room-list")}
              onAbout={() => openPanel("about")}
              onCommunity={() => openCommunity("deck")}
              onLeave={leaveShack}
              onNewsletter={() => openPanel("newsletter")}
              onPrivacy={() => openPanel("privacy")}
            />
          </>
        )}
      </main>

      {reaction && !reactionDismissed && !state.activePanel ? (
        <aside className={styles.reactionNotice} aria-live="polite">
          <button
            className={styles.noticeClose}
            type="button"
            aria-label="Verberg deze aanwijzing"
            onClick={() => setReactionDismissed(true)}
          >
            ×
          </button>
          <p className={styles.noticeEyebrow}>{siteCopy.room.reactionEyebrow}</p>
          <h2>{reaction.title}</h2>
          <p>{reaction.body}</p>
          <div className={styles.noticeActions}>
            <button
              type="button"
              onClick={() => activateHotspot(recommendationHotspot[reaction.id])}
            >
              {reaction.nextLabel}
            </button>
            <button type="button" onClick={() => openPanel("contact")}>
              {siteCopy.room.reactionContactLabel}
            </button>
          </div>
        </aside>
      ) : null}

      {state.activePanel === "room-list" ? (
        <Dialog
          eyebrow={siteCopy.scene.objectMenuLabel}
          title={siteCopy.scene.title}
          description={siteCopy.scene.description}
          onClose={closePanel}
        >
          <RoomObjectList
            discoveredHotspotIds={state.discoveredHotspots}
            onHotspotActivate={activateHotspot}
          />
        </Dialog>
      ) : null}

      {state.activePanel === "sea" ? (
        <Dialog
          eyebrow={siteCopy.sea.dialogEyebrow}
          title={siteCopy.sea.dialogTitle}
          description={siteCopy.sea.dialogDescription}
          onClose={closePanel}
        >
          <SeaCheckInPanel
            value={state.seaState}
            onChange={(seaState) => dispatch({ type: "set-sea-state", seaState })}
            onContinue={activateHotspot}
          />
        </Dialog>
      ) : null}

      {state.activePanel === "journal" && journalView === "landing" ? (
        <Dialog
          eyebrow={siteCopy.journal.dialogEyebrow}
          title={siteCopy.journal.dialogTitle}
          description={siteCopy.journal.dialogDescription}
          onClose={closePanel}
        >
          <JournalLanding
            recommendation={state.recommendation}
            onDrawCard={() => setJournalView("card")}
            onStartSoulCheck={() => setJournalView("soul-check")}
          />
        </Dialog>
      ) : null}

      {state.activePanel === "journal" && journalView === "card" ? (
        <Dialog
          chrome="minimal"
          title={siteCopy.dailyGift.cardDialogTitle}
          onClose={closePanel}
        >
          <DailyCardPanel embedded onClose={closePanel} />
        </Dialog>
      ) : null}

      {state.activePanel === "journal" && journalView === "soul-check" ? (
        <Dialog
          chrome="minimal"
          title={siteCopy.soulCheckPanel.dialogTitle}
          onClose={closePanel}
        >
          <div className={styles.ritualStack}>
            <SoulCheck embedded onComplete={finishSoulCheck} onClose={closePanel} />
            {soulCheck ? (
              <aside className={styles.shareInvitation}>
                <div>
                  <strong>{siteCopy.soulCheckPanel.shareTitle}</strong>
                  <p>{siteCopy.soulCheckPanel.shareBody}</p>
                </div>
                <button type="button" onClick={() => openPanel("contact")}>
                  {siteCopy.soulCheckPanel.shareCtaLabel}
                </button>
              </aside>
            ) : null}
          </div>
        </Dialog>
      ) : null}

      {state.activePanel === "hearth" ? (
        <Dialog
          chrome="minimal"
          title={siteCopy.hearth.dialogTitle}
          onClose={closePanel}
        >
          <HearthPanel embedded onClose={closePanel} />
        </Dialog>
      ) : null}

      {state.activePanel === "menu" ? (
        <Dialog
          chrome="minimal"
          title={siteCopy.dailyGift.recipeDialogTitle}
          onClose={closePanel}
        >
          <DailyRecipePanel embedded onClose={closePanel} />
        </Dialog>
      ) : null}

      {state.activePanel === "boards" ? (
        <Dialog
          chrome="minimal"
          title={siteCopy.boards.dialogTitle}
          onClose={closePanel}
        >
          <BoardsPanel
            embedded
            suggestedDirection={state.recommendation}
            onClose={closePanel}
          />
        </Dialog>
      ) : null}

      {state.activePanel === "contact" ? (
        <Dialog
          size="wide"
          eyebrow={siteCopy.contact.dialogEyebrow}
          title={siteCopy.contact.dialogTitle}
          description={siteCopy.contact.dialogDescription}
          onClose={closePanel}
        >
          <ContactForm
            privacyUrl="/privacy"
            soulCheck={
              soulCheck
                ? { answers: soulCheck.answers, reflection: soulCheck.reflection }
                : undefined
            }
          />
        </Dialog>
      ) : null}

      {state.activePanel === "newsletter" ? (
        <Dialog
          eyebrow={siteCopy.newsletter.dialogEyebrow}
          title={siteCopy.newsletter.dialogTitle}
          description={siteCopy.newsletter.dialogDescription}
          onClose={closePanel}
        >
          <NewsletterForm privacyUrl="/privacy" />
        </Dialog>
      ) : null}

      {state.activePanel === "privacy" ? (
        <Dialog size="wide" title={siteCopy.privacy.dialogTitle} onClose={closePanel}>
          <PrivacyPanel />
        </Dialog>
      ) : null}

      {state.activePanel === "about" ? (
        <Dialog
          title={siteCopy.about.dialogTitle}
          description={siteCopy.about.dialogDescription}
          onClose={closePanel}
        >
          <AboutPanel />
        </Dialog>
      ) : null}
    </div>
  );
}
